<template>
  <div class="quote-editor-page">
    <!-- Header -->
    <header class="editor-header">
      <div class="header-left">
        <router-link to="/quotes" class="back-link">
          <i class="fas fa-arrow-left"></i> Back to Quotes
        </router-link>
        <div class="quote-identity" v-if="quote">
          <h1>{{ quote.quote_number }}</h1>
          <span class="status-badge" :class="quote.status.toLowerCase()">
            {{ quote.status_display }}
          </span>
        </div>
      </div>
      <div class="header-actions" v-if="quote">
        <!-- Revert to Draft button for finalized quotes -->
        <button v-if="quote.status === 'FINALIZED' || quote.status === 'SENT'" 
                @click="revertToDraft" 
                class="btn btn-warning">
          <i class="fas fa-undo"></i> Revert to Draft
        </button>
        
        <button @click="saveChanges" 
                :disabled="saving || !hasChanges || quote.status === 'FINALIZED' || quote.status === 'SENT'" 
                class="btn btn-primary">
          <i v-if="saving" class="fas fa-spinner fa-spin"></i>
          <i v-else class="fas fa-save"></i>
          {{ saving ? 'Saving...' : 'Save Changes' }}
        </button>
        <button @click="exportPDF" class="btn btn-outline">
          <i class="fas fa-file-pdf"></i> Export PDF
        </button>
        <div class="status-dropdown" v-if="quote.status === 'DRAFT'">
          <button @click="finalizeQuote" class="btn btn-success">
            <i class="fas fa-check"></i> Finalize
          </button>
        </div>
      </div>
    </header>
    
    <!-- Finalized Warning Banner -->
    <div v-if="quote && (quote.status === 'FINALIZED' || quote.status === 'SENT')" class="finalized-banner">
      <i class="fas fa-lock"></i>
      <span>This quote is {{ quote.status_display }}. Click "Revert to Draft" to make changes.</span>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <i class="fas fa-spinner fa-spin"></i>
      <span>Loading quote...</span>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-state">
      <i class="fas fa-exclamation-triangle"></i>
      <h3>Error Loading Quote</h3>
      <p>{{ error }}</p>
      <router-link to="/quotes" class="btn btn-primary">Back to Quotes</router-link>
    </div>

    <!-- Editor Content -->
    <div v-else-if="quote" class="editor-content">
      <!-- Sidebar Navigation -->
      <nav class="editor-sidebar">
        <ul class="section-nav">
          <li v-for="section in editableSections" :key="section.key">
            <button 
              @click="activeSection = section.key"
              :class="{ active: activeSection === section.key }"
            >
              <i :class="section.icon"></i>
              {{ section.label }}
            </button>
          </li>
        </ul>
      </nav>

      <!-- Main Editor Panel -->
      <main class="editor-main">
        <!-- Client Info Section -->
        <section v-if="activeSection === 'client'" class="editor-section">
          <h2><i class="fas fa-user-tie"></i> Client Information</h2>
          <div class="form-grid">
            <div class="form-group">
              <label>Client/Agent Name</label>
              <input type="text" v-model="editedQuote.client_name" />
            </div>
            <div class="form-group">
              <label>Contact Name</label>
              <input type="text" v-model="editedQuote.contact_name" />
            </div>
            <div class="form-group">
              <label>Contact Email</label>
              <input type="email" v-model="editedQuote.contact_email" />
            </div>
            <div class="form-group">
              <label>Client Type</label>
              <input type="text" v-model="editedQuote.client_type" placeholder="e.g., franchise, builder" />
            </div>
          </div>
        </section>

        <!-- Title & Executive Summary -->
        <section v-if="activeSection === 'summary'" class="editor-section">
          <h2><i class="fas fa-file-alt"></i> Title & Summary</h2>
          <div class="form-group">
            <label>Proposal Title</label>
            <input type="text" v-model="editedQuote.title" />
          </div>
          <div class="form-group">
            <label>Executive Summary</label>
            <textarea 
              v-model="editedQuote.executive_summary" 
              rows="6"
              placeholder="Enter executive summary..."
            ></textarea>
          </div>
        </section>

        <!-- Case Study Section -->
        <section v-if="activeSection === 'case_study'" class="editor-section">
          <h2><i class="fas fa-chart-line"></i> Case Study</h2>
          <div class="form-grid">
            <div class="form-group">
              <label>Case Study Name</label>
              <input type="text" v-model="editedQuote.case_study_content.name" />
            </div>
            <div class="form-group">
              <label>Industry</label>
              <input type="text" v-model="editedQuote.case_study_content.industry" />
            </div>
          </div>
          
          <h3>Key Metrics</h3>
          <div class="stats-editor" v-if="editedQuote.case_study_content.stats">
            <div 
              v-for="(stat, index) in editedQuote.case_study_content.stats" 
              :key="index" 
              class="stat-item"
            >
              <input type="text" v-model="stat.label" placeholder="Label" />
              <input type="text" v-model="stat.value" placeholder="Value" />
              <button @click="removeStat(index)" class="btn-icon danger">
                <i class="fas fa-times"></i>
              </button>
            </div>
            <button @click="addStat" class="btn btn-sm btn-outline">
              <i class="fas fa-plus"></i> Add Metric
            </button>
          </div>
          
          <div class="form-group">
            <label>Testimonial</label>
            <textarea 
              v-model="editedQuote.case_study_content.testimonial" 
              rows="3"
              placeholder="Client testimonial quote..."
            ></textarea>
          </div>
        </section>

        <!-- Pain Points Section -->
        <section v-if="activeSection === 'pain_points'" class="editor-section">
          <h2><i class="fas fa-exclamation-circle"></i> Pain Points Addressed</h2>
          <div 
            v-for="(point, index) in editedQuote.pain_points_content" 
            :key="index" 
            class="pain-point-item"
          >
            <div class="item-header">
              <span class="item-number">{{ index + 1 }}</span>
              <button @click="removePainPoint(index)" class="btn-icon danger">
                <i class="fas fa-trash"></i>
              </button>
            </div>
            <div class="form-group">
              <label>Pain Point / Problem</label>
              <input type="text" v-model="point.problem" />
            </div>
            <div class="form-group">
              <label>Our Solution</label>
              <textarea v-model="point.solution" rows="2"></textarea>
            </div>
            <div class="form-group">
              <label>Value / Impact</label>
              <input type="text" v-model="point.value" />
            </div>
          </div>
          <button @click="addPainPoint" class="btn btn-outline">
            <i class="fas fa-plus"></i> Add Pain Point
          </button>
        </section>

        <!-- Pricing Section -->
        <section v-if="activeSection === 'pricing'" class="editor-section">
          <h2><i class="fas fa-dollar-sign"></i> Pricing</h2>
          <div class="pricing-editor">
            <div class="form-group">
              <label>Pricing Model</label>
              <input type="text" v-model="editedQuote.pricing_content.model" placeholder="e.g., Per-Entity, Flat Fee" />
            </div>
            <div class="form-grid">
              <div class="form-group">
                <label>Base Price</label>
                <input type="text" v-model="editedQuote.pricing_content.base_price" />
              </div>
              <div class="form-group">
                <label>Per Entity Price</label>
                <input type="text" v-model="editedQuote.pricing_content.per_entity" />
              </div>
            </div>
            <div class="form-group">
              <label>Pricing Notes</label>
              <textarea 
                v-model="editedQuote.pricing_content.notes" 
                rows="3"
                placeholder="Additional pricing details..."
              ></textarea>
            </div>
          </div>
        </section>

        <!-- Implementation Timeline -->
        <section v-if="activeSection === 'implementation'" class="editor-section">
          <h2><i class="fas fa-calendar-alt"></i> Implementation Timeline</h2>
          <div class="phases-editor" v-if="editedQuote.implementation_content.phases">
            <div 
              v-for="(phase, index) in editedQuote.implementation_content.phases" 
              :key="index" 
              class="phase-item"
            >
              <div class="phase-header">
                <span class="phase-number">Phase {{ index + 1 }}</span>
                <button @click="removePhase(index)" class="btn-icon danger">
                  <i class="fas fa-trash"></i>
                </button>
              </div>
              <div class="form-grid">
                <div class="form-group">
                  <label>Phase Name</label>
                  <input type="text" v-model="phase.name" />
                </div>
                <div class="form-group">
                  <label>Duration</label>
                  <input type="text" v-model="phase.duration" placeholder="e.g., Week 1-2" />
                </div>
              </div>
              <div class="form-group">
                <label>Activities (comma-separated)</label>
                <input 
                  type="text" 
                  :value="Array.isArray(phase.activities) ? phase.activities.join(', ') : ''" 
                  @input="phase.activities = $event.target.value.split(',').map(s => s.trim())"
                />
              </div>
            </div>
            <button @click="addPhase" class="btn btn-outline">
              <i class="fas fa-plus"></i> Add Phase
            </button>
          </div>
        </section>

        <!-- Check Definitions Section -->
        <section v-if="activeSection === 'check_definitions'" class="editor-section">
          <h2><i class="fas fa-clipboard-check"></i> Sample Check Definitions</h2>
          <p class="section-note">
            These check definitions will be displayed in the proposal to showcase your capabilities.
          </p>
          <div 
            v-for="(check, index) in editedQuote.check_definitions_content" 
            :key="index" 
            class="check-def-item"
          >
            <div class="item-header">
              <span class="check-type-badge">{{ check.check_type || check.key }}</span>
              <button @click="removeCheckDef(index)" class="btn-icon danger">
                <i class="fas fa-trash"></i>
              </button>
            </div>
            <div class="form-group">
              <label>Check Name</label>
              <input type="text" v-model="check.name" />
            </div>
            <div class="form-group">
              <label>Description</label>
              <textarea v-model="check.description" rows="2"></textarea>
            </div>
          </div>
        </section>

        <!-- Sample Results Section -->
        <section v-if="activeSection === 'sample_results'" class="editor-section">
          <h2><i class="fas fa-chart-bar"></i> Sample Analysis Results</h2>
          <p class="section-note">
            Summary of sample results included in the proposal. Full reports should be attached separately.
          </p>
          <div 
            v-for="(resultGroup, index) in editedQuote.sample_results_content" 
            :key="index" 
            class="result-group"
          >
            <h4>{{ resultGroup.check_definition?.name || 'Check Definition' }}</h4>
            <div class="results-summary">
              <span>{{ resultGroup.sample_results?.length || 0 }} sample reports</span>
            </div>
          </div>
          <p v-if="!editedQuote.sample_results_content?.length" class="empty-note">
            No sample results included. You can add them when generating the quote.
          </p>
        </section>

        <!-- Next Steps Section -->
        <section v-if="activeSection === 'next_steps'" class="editor-section">
          <h2><i class="fas fa-tasks"></i> Next Steps</h2>
          <div 
            v-for="(step, index) in editedQuote.next_steps_content" 
            :key="index" 
            class="next-step-item"
          >
            <div class="item-header">
              <span class="step-number">Step {{ step.step || index + 1 }}</span>
              <button @click="removeNextStep(index)" class="btn-icon danger">
                <i class="fas fa-trash"></i>
              </button>
            </div>
            <div class="form-group">
              <label>Title</label>
              <input type="text" v-model="step.title" />
            </div>
            <div class="form-group">
              <label>Activities</label>
              <textarea 
                :value="Array.isArray(step.activities) ? step.activities.join('\n') : step.description" 
                @input="updateStepActivities(step, $event.target.value)"
                rows="3"
                placeholder="One activity per line"
              ></textarea>
            </div>
          </div>
          <button @click="addNextStep" class="btn btn-outline">
            <i class="fas fa-plus"></i> Add Step
          </button>
        </section>

        <!-- Pilot Process Section -->
        <section v-if="activeSection === 'pilot'" class="editor-section">
          <h2><i class="fas fa-flask"></i> Pilot Process</h2>
          <div class="form-group">
            <label>Pilot Description</label>
            <textarea 
              v-model="editedQuote.pilot_process_content.description" 
              rows="5"
              placeholder="Describe the pilot process and what's included..."
            ></textarea>
          </div>
        </section>

        <!-- Internal Notes Section -->
        <section v-if="activeSection === 'notes'" class="editor-section">
          <h2><i class="fas fa-sticky-note"></i> Internal Notes</h2>
          <p class="section-note">
            These notes are for internal use only and will not appear in the exported PDF.
          </p>
          <div class="form-group">
            <textarea 
              v-model="editedQuote.internal_notes" 
              rows="8"
              placeholder="Add internal notes, reminders, or context..."
            ></textarea>
          </div>
        </section>

        <!-- Attachments Section -->
        <section v-if="activeSection === 'attachments'" class="editor-section">
          <h2><i class="fas fa-paperclip"></i> Attachments</h2>
          <div class="attachments-list" v-if="quote.attachments?.length">
            <div v-for="att in quote.attachments" :key="att.id" class="attachment-item">
              <i class="fas fa-file-pdf"></i>
              <span class="filename">{{ att.filename }}</span>
              <span class="file-size">{{ formatFileSize(att.file_size) }}</span>
              <a :href="att.file_url" target="_blank" class="btn btn-sm btn-outline">
                <i class="fas fa-download"></i>
              </a>
              <button @click="deleteAttachment(att)" class="btn btn-sm btn-danger-outline">
                <i class="fas fa-trash"></i>
              </button>
            </div>
          </div>
          <div v-else class="empty-note">
            No attachments yet. Upload sample analysis reports here.
          </div>
          
          <div class="upload-area">
            <input 
              type="file" 
              ref="fileInput" 
              @change="handleFileUpload" 
              accept=".pdf,.doc,.docx"
              style="display: none;"
            />
            <button @click="$refs.fileInput.click()" class="btn btn-outline">
              <i class="fas fa-upload"></i> Upload Attachment
            </button>
          </div>
        </section>
      </main>
    </div>

    <!-- Unsaved Changes Warning -->
    <div v-if="hasChanges && !saving" class="unsaved-banner">
      <i class="fas fa-exclamation-circle"></i>
      You have unsaved changes
      <button @click="saveChanges" class="btn btn-sm btn-primary">Save Now</button>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import api from '@/services/api';
import { isEqual, cloneDeep } from 'lodash-es';

export default {
  name: 'QuoteEditor',
  setup() {
    const route = useRoute();
    
    const quote = ref(null);
    const editedQuote = ref({});
    const loading = ref(true);
    const saving = ref(false);
    const error = ref(null);
    const activeSection = ref('client');
    const fileInput = ref(null);

    const editableSections = [
      { key: 'client', label: 'Client Info', icon: 'fas fa-user-tie' },
      { key: 'summary', label: 'Title & Summary', icon: 'fas fa-file-alt' },
      { key: 'case_study', label: 'Case Study', icon: 'fas fa-chart-line' },
      { key: 'pain_points', label: 'Pain Points', icon: 'fas fa-exclamation-circle' },
      { key: 'pricing', label: 'Pricing', icon: 'fas fa-dollar-sign' },
      { key: 'implementation', label: 'Timeline', icon: 'fas fa-calendar-alt' },
      { key: 'check_definitions', label: 'Check Definitions', icon: 'fas fa-clipboard-check' },
      { key: 'sample_results', label: 'Sample Results', icon: 'fas fa-chart-bar' },
      { key: 'next_steps', label: 'Next Steps', icon: 'fas fa-tasks' },
      { key: 'pilot', label: 'Pilot Process', icon: 'fas fa-flask' },
      { key: 'notes', label: 'Internal Notes', icon: 'fas fa-sticky-note' },
      { key: 'attachments', label: 'Attachments', icon: 'fas fa-paperclip' }
    ];

    // Check if there are unsaved changes
    const hasChanges = computed(() => {
      if (!quote.value) return false;
      return !isEqual(editedQuote.value, getEditableFields(quote.value));
    });

    const getEditableFields = (q) => ({
      client_name: q.client_name || '',
      contact_name: q.contact_name || '',
      contact_email: q.contact_email || '',
      client_type: q.client_type || '',
      title: q.title || '',
      executive_summary: q.executive_summary || '',
      case_study_content: q.case_study_content || {},
      pain_points_content: q.pain_points_content || [],
      pricing_content: q.pricing_content || {},
      implementation_content: q.implementation_content || {},
      check_definitions_content: q.check_definitions_content || [],
      sample_results_content: q.sample_results_content || [],
      next_steps_content: q.next_steps_content || [],
      pilot_process_content: q.pilot_process_content || {},
      internal_notes: q.internal_notes || ''
    });

    // Transform case study stats from object to array format for editing
    const normalizeCaseStudyStats = (stats) => {
      if (!stats) return [];
      if (Array.isArray(stats)) return stats;
      
      // Convert object format to array format
      const labelMap = {
        'locations': 'Locations',
        'documents_processed': 'Documents',
        'processing_time_days': 'Days to Process',
        'manual_time_estimate_days': 'Manual Estimate (Days)',
        'time_savings_percent': 'Time Savings'
      };
      
      return Object.entries(stats).map(([key, value]) => ({
        label: labelMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        value: typeof value === 'number' ? value.toLocaleString() + (key.includes('percent') ? '%' : '') : String(value)
      }));
    };

    const loadQuote = async () => {
      loading.value = true;
      error.value = null;
      
      try {
        const response = await api.get(`/quotes/${route.params.id}/`);
        quote.value = response.data.quote;
        editedQuote.value = cloneDeep(getEditableFields(quote.value));
        
        // Normalize case study stats from object to array format
        if (editedQuote.value.case_study_content) {
          editedQuote.value.case_study_content.stats = normalizeCaseStudyStats(
            editedQuote.value.case_study_content.stats
          );
        } else {
          editedQuote.value.case_study_content = { stats: [] };
        }
        
        if (!editedQuote.value.implementation_content.phases) {
          editedQuote.value.implementation_content.phases = [];
        }
      } catch (err) {
        console.error('Error loading quote:', err);
        error.value = err.response?.data?.error || 'Failed to load quote';
      } finally {
        loading.value = false;
      }
    };

    const saveChanges = async () => {
      saving.value = true;
      
      try {
        const response = await api.patch(`/quotes/${route.params.id}/`, editedQuote.value);
        quote.value = response.data.quote;
        editedQuote.value = cloneDeep(getEditableFields(quote.value));
        
        // Re-normalize case study stats after save
        if (editedQuote.value.case_study_content) {
          editedQuote.value.case_study_content.stats = normalizeCaseStudyStats(
            editedQuote.value.case_study_content.stats
          );
        }
        
        console.log('Quote saved successfully');
      } catch (err) {
        console.error('Error saving quote:', err);
        alert('Failed to save changes: ' + (err.response?.data?.error || err.message));
      } finally {
        saving.value = false;
      }
    };

    const finalizeQuote = async () => {
      if (!confirm('Finalize this quote? You will need to revert to draft to make further edits.')) return;
      
      try {
        await api.post(`/quotes/${route.params.id}/finalize/`);
        await loadQuote();
      } catch (err) {
        console.error('Error finalizing quote:', err);
        alert('Failed to finalize quote');
      }
    };

    const revertToDraft = async () => {
      if (!confirm('Revert this quote to draft? This will allow you to make edits again.')) return;
      
      try {
        await api.patch(`/quotes/${route.params.id}/`, { status: 'DRAFT' });
        await loadQuote();
      } catch (err) {
        console.error('Error reverting to draft:', err);
        alert('Failed to revert quote to draft');
      }
    };

    const exportPDF = async () => {
      try {
        const response = await api.get(`/quotes/${route.params.id}/export-pdf/`);
        
        const printWindow = window.open('', '_blank');
        printWindow.document.write(response.data);
        printWindow.document.close();
        
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };
      } catch (err) {
        console.error('Error exporting PDF:', err);
        alert('Failed to export PDF');
      }
    };

    // Helper functions for editing arrays
    const addStat = () => {
      if (!editedQuote.value.case_study_content.stats) {
        editedQuote.value.case_study_content.stats = [];
      }
      editedQuote.value.case_study_content.stats.push({ label: '', value: '' });
    };

    const removeStat = (index) => {
      editedQuote.value.case_study_content.stats.splice(index, 1);
    };

    const addPainPoint = () => {
      editedQuote.value.pain_points_content.push({
        problem: '',
        solution: '',
        value: ''
      });
    };

    const removePainPoint = (index) => {
      editedQuote.value.pain_points_content.splice(index, 1);
    };

    const addPhase = () => {
      if (!editedQuote.value.implementation_content.phases) {
        editedQuote.value.implementation_content.phases = [];
      }
      editedQuote.value.implementation_content.phases.push({
        name: '',
        duration: '',
        activities: []
      });
    };

    const removePhase = (index) => {
      editedQuote.value.implementation_content.phases.splice(index, 1);
    };

    const removeCheckDef = (index) => {
      editedQuote.value.check_definitions_content.splice(index, 1);
    };

    const addNextStep = () => {
      const nextNum = editedQuote.value.next_steps_content.length + 1;
      editedQuote.value.next_steps_content.push({
        step: nextNum,
        title: '',
        activities: []
      });
    };

    const removeNextStep = (index) => {
      editedQuote.value.next_steps_content.splice(index, 1);
    };

    const updateStepActivities = (step, value) => {
      step.activities = value.split('\n').filter(s => s.trim());
    };

    const handleFileUpload = async (event) => {
      const file = event.target.files[0];
      if (!file) return;
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('attachment_type', 'SAMPLE_REPORT');
      
      try {
        await api.post(`/quotes/${route.params.id}/attachments/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        await loadQuote();
      } catch (err) {
        console.error('Error uploading file:', err);
        alert('Failed to upload file');
      }
    };

    const deleteAttachment = async (att) => {
      if (!confirm(`Delete ${att.filename}?`)) return;
      
      try {
        await api.delete(`/quotes/${route.params.id}/attachments/${att.id}/`);
        await loadQuote();
      } catch (err) {
        console.error('Error deleting attachment:', err);
        alert('Failed to delete attachment');
      }
    };

    const formatFileSize = (bytes) => {
      if (!bytes) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    onMounted(() => {
      loadQuote();
    });

    // Warn before leaving with unsaved changes
    window.addEventListener('beforeunload', (e) => {
      if (hasChanges.value) {
        e.preventDefault();
        e.returnValue = '';
      }
    });

    return {
      quote,
      editedQuote,
      loading,
      saving,
      error,
      activeSection,
      editableSections,
      hasChanges,
      fileInput,
      loadQuote,
      saveChanges,
      finalizeQuote,
      revertToDraft,
      exportPDF,
      addStat,
      removeStat,
      addPainPoint,
      removePainPoint,
      addPhase,
      removePhase,
      removeCheckDef,
      addNextStep,
      removeNextStep,
      updateStepActivities,
      handleFileUpload,
      deleteAttachment,
      formatFileSize
    };
  }
};
</script>

<style scoped>
.quote-editor-page {
  min-height: 100vh;
  background: #f5f7fa;
}

.editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 2rem;
}

.back-link {
  color: #666;
  text-decoration: none;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.back-link:hover {
  color: #667eea;
}

.quote-identity {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.quote-identity h1 {
  font-size: 1.5rem;
  margin: 0;
  color: #1a1a2e;
}

.status-badge {
  padding: 0.35rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
}

.status-badge.draft {
  background: #fff3cd;
  color: #856404;
}

.status-badge.finalized {
  background: #d4edda;
  color: #155724;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
}

.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  text-align: center;
}

.loading-state i,
.error-state i {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.loading-state i {
  color: #667eea;
}

.error-state i {
  color: #e53e3e;
}

.editor-content {
  display: flex;
  min-height: calc(100vh - 80px);
}

.editor-sidebar {
  width: 240px;
  background: white;
  border-right: 1px solid #e2e8f0;
  padding: 1rem 0;
  position: sticky;
  top: 80px;
  height: calc(100vh - 80px);
  overflow-y: auto;
}

.section-nav {
  list-style: none;
  padding: 0;
  margin: 0;
}

.section-nav li button {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 1.5rem;
  border: none;
  background: none;
  text-align: left;
  font-size: 0.9rem;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
}

.section-nav li button:hover {
  background: #f5f7fa;
  color: #1a1a2e;
}

.section-nav li button.active {
  background: #eef2ff;
  color: #667eea;
  border-right: 3px solid #667eea;
}

.section-nav li button i {
  width: 20px;
  text-align: center;
}

.editor-main {
  flex: 1;
  padding: 2rem;
  overflow-y: auto;
}

.editor-section {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.editor-section h2 {
  font-size: 1.35rem;
  color: #1a1a2e;
  margin: 0 0 1.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.editor-section h2 i {
  color: #667eea;
}

.editor-section h3 {
  font-size: 1.1rem;
  color: #333;
  margin: 1.5rem 0 1rem 0;
}

.section-note {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 1.5rem;
  padding: 0.75rem 1rem;
  background: #f5f7fa;
  border-radius: 6px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  font-weight: 500;
  color: #333;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.95rem;
  transition: border-color 0.2s;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #667eea;
}

.stats-editor,
.list-editor {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.stat-item,
.list-item {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.stat-item input {
  flex: 1;
}

.list-item input {
  flex: 1;
}

.btn-icon {
  padding: 0.5rem;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 4px;
  color: #666;
}

.btn-icon:hover {
  background: #f0f0f5;
}

.btn-icon.danger:hover {
  background: #ffebee;
  color: #c62828;
}

.pain-point-item,
.phase-item,
.check-def-item,
.next-step-item {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.item-header,
.phase-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.item-number,
.phase-number,
.step-number {
  font-weight: 600;
  color: #667eea;
}

.check-type-badge {
  background: #667eea;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
}

.result-group {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.result-group h4 {
  margin: 0 0 0.5rem 0;
  color: #333;
}

.results-summary {
  font-size: 0.9rem;
  color: #666;
}

.empty-note {
  color: #999;
  font-style: italic;
  padding: 1rem;
  text-align: center;
  background: #f8f9fa;
  border-radius: 8px;
}

.attachments-list {
  margin-bottom: 1.5rem;
}

.attachment-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  border-radius: 6px;
  margin-bottom: 0.5rem;
}

.attachment-item i {
  color: #e53e3e;
  font-size: 1.25rem;
}

.attachment-item .filename {
  flex: 1;
  font-weight: 500;
}

.attachment-item .file-size {
  color: #999;
  font-size: 0.85rem;
}

.upload-area {
  padding: 2rem;
  border: 2px dashed #ddd;
  border-radius: 8px;
  text-align: center;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.25rem;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s;
}

.btn-sm {
  padding: 0.4rem 0.75rem;
  font-size: 0.85rem;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #5568d3;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-success {
  background: #28a745;
  color: white;
}

.btn-success:hover {
  background: #218838;
}

.btn-warning {
  background: #ffc107;
  color: #212529;
}

.btn-warning:hover {
  background: #e0a800;
}

.finalized-banner {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 2rem;
  background: #fff3cd;
  border-bottom: 1px solid #ffc107;
  color: #856404;
  font-size: 0.95rem;
}

.finalized-banner i {
  font-size: 1.1rem;
}

.btn-outline {
  background: transparent;
  border: 1px solid #ddd;
  color: #666;
}

.btn-outline:hover {
  background: #f5f5f5;
  border-color: #ccc;
}

.btn-danger-outline {
  background: transparent;
  border: 1px solid #ffcdd2;
  color: #c62828;
}

.btn-danger-outline:hover {
  background: #ffebee;
}

.unsaved-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1rem 2rem;
  background: #fff3cd;
  border-top: 1px solid #ffc107;
  display: flex;
  align-items: center;
  gap: 1rem;
  justify-content: center;
  z-index: 200;
}

.unsaved-banner i {
  color: #856404;
}

@media (max-width: 900px) {
  .editor-content {
    flex-direction: column;
  }
  
  .editor-sidebar {
    width: 100%;
    height: auto;
    position: static;
    border-right: none;
    border-bottom: 1px solid #e2e8f0;
  }
  
  .section-nav {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 0 1rem;
  }
  
  .section-nav li button {
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
    border-radius: 20px;
    border: 1px solid #e2e8f0;
  }
  
  .section-nav li button.active {
    border-right: none;
    border-color: #667eea;
  }
  
  .form-grid {
    grid-template-columns: 1fr;
  }
}
</style>

